import * as React from 'react'
import { Plus, Search } from 'lucide-react'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/common/ui/table'
import { Button } from '@/common/ui/button'
import { LabelInput } from '@/common/ui/LabelInput'
import { NAVIGATION_COPY } from '@/locales/en'
import { Account } from '@/common/types'
import { ClientStatus } from '../types'
import { ClientStatusBadge } from './ClientStatusBadge'
import { groupClientsByStatus } from '../utils/groupClientsByStatus'
import { formatDisplayName } from '../utils/formatDisplayName'
import { getClientStatus } from '../utils/getClientStatus'

export type InProgressTableProps = {
    searchTerm?: string
    onSearchChange?: (value: string) => void
    clients?: Account[]
    clientCountLabel?: string
    showClientCount?: boolean
    onResume?: (accountId: string) => void
    onAddClient?: () => void
}

export function InProgressTable({
    searchTerm = '',
    onSearchChange = () => {},
    clients = [],
    clientCountLabel = '',
    showClientCount = false,
    onResume = () => {},
    onAddClient = () => {},
}: InProgressTableProps) {
    const hasSearchResults = clients.length > 0
    const groupedClients = groupClientsByStatus(clients)

    const renderAddClientButton = () => (
        <Button variant="secondary" size="lg" aria-label="Add client" className="px-6 py-3" onClick={onAddClient}>
            <span>{NAVIGATION_COPY.adviserClients.addClient}</span>
            <Plus size={16} />
        </Button>
    )

    return (
        <div className="w-full bg-white rounded-tl-lg rounded-tr-lg flex flex-col gap-8">
            <div className="self-stretch flex flex-col gap-6">
                <div className="self-stretch flex items-center gap-4">
                    <div className="w-full max-w-xl">
                        <div className="relative w-full">
                            <div className="pointer-events-none absolute inset-y-0 left-4 flex items-center">
                                <Search size={16} className="text-brand-primary-forest" />
                            </div>
                            <LabelInput
                                id="in-progress-search"
                                label={NAVIGATION_COPY.adviserClients.searchLabel}
                                labelClassName="sr-only"
                                className="pl-10 bg-white border-neutral-300 text-sm text-black placeholder:text-content-secondary"
                                value={searchTerm}
                                onChange={(event) => onSearchChange(event.target.value)}
                                placeholder={NAVIGATION_COPY.adviserClients.searchPlaceholder}
                            />
                        </div>
                    </div>
                </div>

                {showClientCount && hasSearchResults && (
                    <div className="flex items-center justify-between gap-6">
                        <div className="text-black text-sm font-medium leading-5">{clientCountLabel}</div>
                        {renderAddClientButton()}
                    </div>
                )}

                {!hasSearchResults ? (
                    <div className="self-stretch flex flex-col items-start">
                        <div className="justify-start text-black text-sm font-medium font-['Inter'] leading-5">
                            {NAVIGATION_COPY.adviserClients.noClientsFound}
                        </div>
                        <div className="justify-start text-content-secondary text-sm font-normal font-['Inter'] leading-5">
                            {NAVIGATION_COPY.adviserClients.tryChangingSearchTerms}
                        </div>
                    </div>
                ) : groupedClients.length === 0 ? (
                    <div className="w-full bg-white rounded-lg flex flex-col items-start p-6">
                        <div className="text-black text-sm font-medium leading-5">
                            {NAVIGATION_COPY.adviserClients.inProgress.emptyState}
                        </div>
                    </div>
                ) : (
                    <div className="self-stretch bg-white flex flex-col overflow-hidden rounded-lg md:border border-stone-200">
                        <Table className="min-w-full">
                            <TableHeader className="md:bg-stone-50">
                                <TableRow className="hover:bg-stone-50">
                                    <TableHead className="px-2 md:px-6 py-3 text-xs font-medium text-content-secondary md:hidden">
                                        {NAVIGATION_COPY.adviserClients.accountName}
                                    </TableHead>
                                    <TableHead className="hidden md:table-cell w-28 px-6 py-3 text-xs font-medium text-content-secondary">
                                        {NAVIGATION_COPY.adviserClients.firstName}
                                    </TableHead>
                                    <TableHead className="hidden md:table-cell w-28 min-w-24 px-6 py-3 text-xs font-medium text-content-secondary">
                                        {NAVIGATION_COPY.adviserClients.lastName}
                                    </TableHead>
                                    <TableHead className="w-28 min-w-24 px-2 md:px-6 py-3 text-xs font-medium text-content-secondary">
                                        {NAVIGATION_COPY.adviserClients.type}
                                    </TableHead>
                                    <TableHead className="flex-1 min-w-24 px-2 md:px-6 py-3 text-xs font-medium text-content-secondary">
                                        {NAVIGATION_COPY.adviserClients.email}
                                    </TableHead>
                                    <TableHead className="w-72 min-w-48 px-2 md:px-6 py-3 text-xs font-medium text-content-secondary">
                                        {NAVIGATION_COPY.adviserClients.statusColumn}
                                    </TableHead>
                                    <TableHead className="flex-1 min-w-24 px-2 md:px-6 py-3 text-xs font-medium text-content-secondary">
                                        {NAVIGATION_COPY.adviserClients.actionColumn}
                                    </TableHead>
                                </TableRow>
                            </TableHeader>
                            <TableBody>
                                {groupedClients.map((group) => (
                                    <React.Fragment key={group.category}>
                                        <TableRow
                                            key={`header-${group.category}`}
                                            className="bg-neutral-50 border-l border-r border-border-divider hover:bg-neutral-50"
                                        >
                                            <TableCell colSpan={7} className="px-6 py-3 border-b border-border-divider">
                                                <div className="flex items-center gap-2">
                                                    <div className="text-content-tertiary text-xs font-medium leading-5">
                                                        {group.categoryLabel}
                                                    </div>
                                                    <div className="w-1 h-1 bg-content-tertiary rounded-full" />
                                                    <div className="text-content-tertiary text-xs font-medium leading-5">
                                                        {NAVIGATION_COPY.adviserClients.inProgress.clientsCount.replace(
                                                            '{count}',
                                                            String(group.count),
                                                        )}
                                                    </div>
                                                </div>
                                            </TableCell>
                                        </TableRow>
                                        {group.clients.map((client) => {
                                            const { firstName, lastName } = formatDisplayName(client.accountName)
                                            const status = getClientStatus(client.accountStatus)
                                            const showResumeButton = status === ClientStatus.KYC_IN_PROGRESS

                                            return (
                                                <TableRow
                                                    key={client.accountId}
                                                    className="border-b border-border-divider bg-white hover:bg-stone-50"
                                                >
                                                    <TableCell className="px-2 md:px-6 py-3 bg-white border-b border-border-divider">
                                                        <span className="md:hidden text-black text-sm font-medium leading-5">
                                                            {firstName} {lastName}
                                                        </span>
                                                        <span className="hidden md:inline text-black text-sm font-medium leading-5">
                                                            {firstName}
                                                        </span>
                                                    </TableCell>
                                                    <TableCell className="hidden md:table-cell px-6 py-3">
                                                        <div className="text-black text-sm font-medium leading-5">
                                                            {lastName}
                                                        </div>
                                                    </TableCell>
                                                    <TableCell className="px-2 md:px-6 py-3">
                                                        <div className="text-black text-sm font-medium leading-5">
                                                            {NAVIGATION_COPY.accountTypes[client.accountType]}
                                                        </div>
                                                    </TableCell>
                                                    <TableCell className="px-2 md:px-6 py-3">
                                                        <div className="text-black text-sm font-medium leading-5">
                                                            —
                                                        </div>
                                                    </TableCell>
                                                    <TableCell className="px-2 md:px-6 py-3">
                                                        {status && <ClientStatusBadge status={status} />}
                                                    </TableCell>
                                                    <TableCell className="px-2 md:px-6 py-3">
                                                        {showResumeButton && (
                                                            <Button
                                                                variant="outline"
                                                                size="sm"
                                                                className="px-3 py-1.5"
                                                                onClick={() => onResume(client.accountId)}
                                                            >
                                                                {NAVIGATION_COPY.adviserClients.actions.resume}
                                                            </Button>
                                                        )}
                                                    </TableCell>
                                                </TableRow>
                                            )
                                        })}
                                    </React.Fragment>
                                ))}
                            </TableBody>
                        </Table>
                    </div>
                )}
            </div>
        </div>
    )
}
